'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import HeroSection from './HeroSection';
import ResourceCard from './ResourceCard';
import AssessmentToolCard from './AssessmentToolCard';
import TrendCard from './TrendCard';
import GlossaryItem from './GlossaryItem';
import ImplementationPhaseCard from './ImplementationPhaseCard';

interface Resource {
  id: number;
  title: string;
  description: string;
  category: string;
  readTime: string;
  image: string;
  alt: string;
  downloadable: boolean;
}

interface AssessmentTool {
  id: number;
  icon: string;
  title: string;
  description: string;
  features: string[];
  estimatedTime: string;
  buttonText: string;
}

interface Trend {
  id: number;
  trend: string;
  impact: 'High' | 'Medium' | 'Low';
  timeframe: string;
  description: string;
  industries: string[];
}

interface GlossaryTerm {
  id: number;
  term: string;
  definition: string;
  businessContext: string;
  relatedTerms: string[];
}

interface ImplementationPhase {
  id: number;
  phase: string;
  duration: string;
  description: string;
  keyActivities: string[];
  deliverables: string[];
}

const AIHubInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeTab, setActiveTab] = useState('guides');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [expandedGlossaryId, setExpandedGlossaryId] = useState<number | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const resources: Resource[] = [
  {
    id: 1,
    title: "Complete Guide to AI Implementation for Growing Businesses",
    description: "A comprehensive roadmap covering strategy, technology selection, team preparation, and measuring success in your AI transformation journey.",
    category: "Implementation Guide",
    readTime: "25 min read",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c7085398-1764660533982.png",
    alt: "Modern office workspace with laptop displaying AI analytics dashboard and colorful data visualizations",
    downloadable: true
  },
  {
    id: 2,
    title: "Marketing Automation ROI Calculator & Strategy Framework",
    description: "Calculate your potential returns and build a data-driven marketing automation strategy that aligns with your business goals.",
    category: "ROI & Strategy",
    readTime: "18 min read",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1997a6356-1764656421129.png",
    alt: "Business professional analyzing marketing metrics on multiple computer screens with graphs and charts",
    downloadable: true
  },
  {
    id: 3,
    title: "Digital Transformation Roadmap: From Vision to Execution",
    description: "Step-by-step guidance for planning and executing successful digital transformation initiatives across your organization.",
    category: "Transformation",
    readTime: "30 min read",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b9a60cec-1767962172889.png",
    alt: "Team collaboration session with digital transformation roadmap displayed on large screen",
    downloadable: true
  },
  {
    id: 4,
    title: "AI Ethics & Governance: Building Trust in Automation",
    description: "Essential frameworks for responsible AI implementation, data privacy, and maintaining customer trust in automated systems.",
    category: "Ethics & Governance",
    readTime: "20 min read",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_16b9f583c-1764668471730.png",
    alt: "Professional woman in business attire presenting AI ethics framework on whiteboard",
    downloadable: false
  },
  {
    id: 5,
    title: "Customer Experience Automation: Best Practices 2026",
    description: "Latest strategies for using AI to enhance customer journeys while maintaining the human touch that builds loyalty.",
    category: "Customer Experience",
    readTime: "22 min read",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a63d8e65-1764642435785.png",
    alt: "Customer service representative using AI-powered interface to assist clients",
    downloadable: false
  },
  {
    id: 6,
    title: "Data Strategy for AI Success: Collection to Insights",
    description: "Build a robust data foundation that powers effective AI applications and delivers actionable business intelligence.",
    category: "Data Strategy",
    readTime: "28 min read",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c3895ecc-1764656426788.png",
    alt: "Data analyst reviewing complex data visualizations and AI model performance metrics",
    downloadable: true
  }];


  const assessmentTools: AssessmentTool[] = [
  {
    id: 1,
    icon: "ChartBarIcon",
    title: "AI Readiness Assessment",
    description: "Evaluate your organization's preparedness for AI adoption across technology, culture, and processes.",
    features: [
    "Comprehensive 50-point evaluation",
    "Personalized recommendations",
    "Gap analysis with action items",
    "Benchmark against industry standards"],

    estimatedTime: "15-20 minutes",
    buttonText: "Start Assessment"
  },
  {
    id: 2,
    icon: "CogIcon",
    title: "Automation Opportunity Analyzer",
    description: "Identify high-impact processes in your business that are prime candidates for automation.",
    features: [
    "Process complexity scoring",
    "ROI potential calculation",
    "Implementation difficulty rating",
    "Priority ranking system"],

    estimatedTime: "10-15 minutes",
    buttonText: "Analyze Processes"
  },
  {
    id: 3,
    icon: "CurrencyDollarIcon",
    title: "ROI Projection Calculator",
    description: "Calculate expected returns from AI and automation investments based on your specific business metrics.",
    features: [
    "Customizable input parameters",
    "Multi-year projection modeling",
    "Cost-benefit breakdown",
    "Payback period estimation"],

    estimatedTime: "8-12 minutes",
    buttonText: "Calculate ROI"
  },
  {
    id: 4,
    icon: "ClockIcon",
    title: "Implementation Timeline Planner",
    description: "Generate a realistic project timeline based on your business size, complexity, and available resources.",
    features: [
    "Phase-by-phase breakdown",
    "Resource allocation guidance",
    "Risk factor consideration",
    "Milestone tracking framework"],

    estimatedTime: "12-18 minutes",
    buttonText: "Plan Timeline"
  }];


  const trends: Trend[] = [
  {
    id: 1,
    trend: "Generative AI for Content Personalization",
    impact: "High",
    timeframe: "Q1-Q2 2026",
    description: "AI-powered content generation will enable hyper-personalized marketing at scale, creating unique experiences for each customer segment.",
    industries: ["Marketing", "E-commerce", "Media", "Education"]
  },
  {
    id: 2,
    trend: "Autonomous Business Process Optimization",
    impact: "High",
    timeframe: "Q2-Q3 2026",
    description: "Self-learning systems that continuously optimize workflows without human intervention, adapting to changing business conditions in real-time.",
    industries: ["Manufacturing", "Logistics", "Finance", "Healthcare"]
  },
  {
    id: 3,
    trend: "Conversational AI for Enterprise Operations",
    impact: "Medium",
    timeframe: "Q1-Q4 2026",
    description: "Advanced chatbots and voice assistants handling complex business operations, from customer service to internal process management.",
    industries: ["Retail", "Banking", "Telecom", "Hospitality"]
  },
  {
    id: 4,
    trend: "Predictive Analytics for Strategic Planning",
    impact: "High",
    timeframe: "Q2-Q4 2026",
    description: "AI models that forecast market trends, customer behavior, and business outcomes with unprecedented accuracy for strategic decision-making.",
    industries: ["Finance", "Retail", "Real Estate", "Technology"]
  },
  {
    id: 5,
    trend: "Edge AI for Real-Time Decision Making",
    impact: "Medium",
    timeframe: "Q3-Q4 2026",
    description: "Processing AI workloads at the edge for instant responses in IoT devices, autonomous systems, and distributed operations.",
    industries: ["Manufacturing", "Transportation", "Agriculture", "Energy"]
  },
  {
    id: 6,
    trend: "Ethical AI & Explainability Standards",
    impact: "High",
    timeframe: "Q1-Q3 2026",
    description: "Industry-wide adoption of transparent AI systems with clear decision-making processes and bias mitigation frameworks.",
    industries: ["Healthcare", "Finance", "Legal", "Government"]
  }];


  const glossaryTerms: GlossaryTerm[] = [
  {
    id: 1,
    term: "Machine Learning (ML)",
    definition: "A subset of AI that enables systems to learn and improve from experience without being explicitly programmed, using algorithms to identify patterns in data.",
    businessContext: "ML powers recommendation engines, fraud detection, customer segmentation, and predictive maintenance, helping businesses make data-driven decisions automatically.",
    relatedTerms: ["Deep Learning", "Neural Networks", "Supervised Learning"]
  },
  {
    id: 2,
    term: "Natural Language Processing (NLP)",
    definition: "Technology that enables computers to understand, interpret, and generate human language in a valuable way.",
    businessContext: "NLP drives chatbots, sentiment analysis, automated email responses, and content generation, improving customer communication and reducing manual workload.",
    relatedTerms: ["Sentiment Analysis", "Text Mining", "Language Models"]
  },
  {
    id: 3,
    term: "Robotic Process Automation (RPA)",
    definition: "Software robots that automate repetitive, rule-based tasks typically performed by humans, mimicking user actions across applications.",
    businessContext: "RPA streamlines data entry, invoice processing, report generation, and compliance tasks, freeing employees for higher-value strategic work.",
    relatedTerms: ["Process Mining", "Intelligent Automation", "Workflow Automation"]
  },
  {
    id: 4,
    term: "Predictive Analytics",
    definition: "Using historical data, statistical algorithms, and machine learning to identify the likelihood of future outcomes based on patterns.",
    businessContext: "Businesses use predictive analytics for demand forecasting, customer churn prediction, inventory optimization, and risk assessment to stay ahead of market changes.",
    relatedTerms: ["Data Mining", "Forecasting", "Statistical Modeling"]
  },
  {
    id: 5,
    term: "Computer Vision",
    definition: "AI technology that enables machines to interpret and understand visual information from the world, including images and videos.",
    businessContext: "Applied in quality control, facial recognition, autonomous vehicles, medical imaging, and retail analytics to automate visual inspection and analysis.",
    relatedTerms: ["Image Recognition", "Object Detection", "Visual Search"]
  },
  {
    id: 6,
    term: "Deep Learning",
    definition: "Advanced machine learning using neural networks with multiple layers to process complex patterns in large amounts of data.",
    businessContext: "Powers advanced applications like voice assistants, image recognition, autonomous systems, and sophisticated recommendation engines for competitive advantage.",
    relatedTerms: ["Neural Networks", "Convolutional Networks", "Transfer Learning"]
  }];


  const implementationPhases: ImplementationPhase[] = [
  {
    id: 1,
    phase: "Phase 1: Discovery & Assessment",
    duration: "2-4 weeks",
    description: "Comprehensive evaluation of current state, identifying opportunities, and defining clear objectives for AI implementation.",
    keyActivities: [
    "Business process mapping and analysis",
    "Technology infrastructure assessment",
    "Stakeholder interviews and requirements gathering",
    "Opportunity identification and prioritization"],

    deliverables: [
    "Current state assessment report",
    "AI readiness scorecard",
    "Prioritized opportunity list",
    "Initial project roadmap"]

  },
  {
    id: 2,
    phase: "Phase 2: Strategy & Planning",
    duration: "3-6 weeks",
    description: "Developing detailed implementation strategy, selecting technologies, and creating comprehensive project plans.",
    keyActivities: [
    "Solution architecture design",
    "Technology vendor evaluation and selection",
    "Resource planning and team formation",
    "Risk assessment and mitigation planning"],

    deliverables: [
    "Detailed implementation strategy",
    "Technology selection report",
    "Project timeline and milestones",
    "Budget and resource allocation plan"]

  },
  {
    id: 3,
    phase: "Phase 3: Pilot Implementation",
    duration: "6-12 weeks",
    description: "Building and testing initial AI solutions in controlled environment to validate approach and refine strategy.",
    keyActivities: [
    "Proof of concept development",
    "Data preparation and model training",
    "Integration with existing systems",
    "User acceptance testing"],

    deliverables: [
    "Working pilot solution",
    "Performance metrics and KPIs",
    "User feedback and refinements",
    "Lessons learned documentation"]

  },
  {
    id: 4,
    phase: "Phase 4: Full Deployment",
    duration: "8-16 weeks",
    description: "Rolling out validated solutions across the organization with comprehensive training and change management.",
    keyActivities: [
    "Production environment setup",
    "Full-scale system integration",
    "Employee training and onboarding",
    "Change management execution"],

    deliverables: [
    "Production-ready AI systems",
    "Training materials and documentation",
    "Support and maintenance procedures",
    "Performance monitoring dashboard"]

  },
  {
    id: 5,
    phase: "Phase 5: Optimization & Scale",
    duration: "Ongoing",
    description: "Continuous improvement, performance optimization, and expansion of AI capabilities across additional use cases.",
    keyActivities: [
    "Performance monitoring and analysis",
    "Model retraining and refinement",
    "Identifying new automation opportunities",
    "Scaling successful implementations"],

    deliverables: [
    "Monthly performance reports",
    "Optimization recommendations",
    "Expansion roadmap",
    "ROI tracking and reporting"]

  }];


  const categories = ['All', 'Implementation Guide', 'ROI & Strategy', 'Transformation', 'Ethics & Governance', 'Customer Experience', 'Data Strategy'];

  const filteredResources = selectedCategory === 'All' ?
  resources :
  resources.filter((resource) => resource.category === selectedCategory);

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="h-16" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
          <div className="animate-pulse space-y-8">
            <div className="h-64 bg-muted rounded-lg" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) =>
              <div key={i} className="h-96 bg-muted rounded-lg" />
              )}
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      <div className="h-16" />
      
      <HeroSection />
      
      {/* Interactive Assessment Tools Section */}
      <section className="py-16 lg:py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
              <Icon name="BeakerIcon" size={20} className="text-primary" />
              <span className="text-sm font-semibold text-primary">Interactive Tools</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-display font-bold text-foreground mb-4">
              Assess Your AI Readiness
            </h2>
            <p className="text-lg text-muted-foreground">
              Use our interactive tools to evaluate your business, identify opportunities, and plan your AI transformation journey with confidence.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {assessmentTools.map((tool) =>
            <AssessmentToolCard key={tool.id} {...tool} />
            )}
          </div>
        </div>
      </section>
      
      {/* Resource Library Section */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="inline-flex items-center space-x-2 bg-accent/10 px-4 py-2 rounded-full mb-4">
              <Icon name="BookOpenIcon" size={20} className="text-accent" />
              <span className="text-sm font-semibold text-accent">Knowledge Library</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-display font-bold text-foreground mb-4">
              Comprehensive AI Resources
            </h2>
            <p className="text-lg text-muted-foreground">
              Deep-dive guides, frameworks, and best practices to accelerate your AI implementation journey.
            </p>
          </div>
          
          {/* Category Filter */}
          <div className="flex flex-wrap gap-3 justify-center mb-12">
            {categories.map((category) =>
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
              selectedCategory === category ?
              'bg-primary text-white shadow-md' :
              'bg-card text-foreground hover:bg-muted border border-border'}`
              }>

                {category}
              </button>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredResources.map((resource) =>
            <ResourceCard key={resource.id} {...resource} />
            )}
          </div>
        </div>
      </section>
      
      {/* AI Trends Section */}
      <section className="py-16 lg:py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="inline-flex items-center space-x-2 bg-brand-purple/10 px-4 py-2 rounded-full mb-4">
              <Icon name="TrendingUpIcon" size={20} className="text-brand-purple" />
              <span className="text-sm font-semibold text-brand-purple">Industry Insights</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-display font-bold text-foreground mb-4">
              AI Trends Shaping 2026
            </h2>
            <p className="text-lg text-muted-foreground">
              Stay ahead with our analysis of emerging AI trends and their business impact across industries.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {trends.map((trend) =>
            <TrendCard key={trend.id} {...trend} />
            )}
          </div>
        </div>
      </section>
      
      {/* AI Glossary Section */}
      <section className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-success/10 px-4 py-2 rounded-full mb-4">
              <Icon name="AcademicCapIcon" size={20} className="text-success" />
              <span className="text-sm font-semibold text-success">Learn the Language</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-display font-bold text-foreground mb-4">
              AI Terms Glossary
            </h2>
            <p className="text-lg text-muted-foreground">
              Understand key AI concepts with clear definitions and practical business context.
            </p>
          </div>
          
          <div className="space-y-4">
            {glossaryTerms.map((term) =>
            <GlossaryItem
              key={term.id}
              {...term}
              isExpanded={expandedGlossaryId === term.id}
              onToggle={() => setExpandedGlossaryId(expandedGlossaryId === term.id ? null : term.id)} />

            )}
          </div>
        </div>
      </section>
      
      {/* Implementation Timeline Section */}
      <section className="py-16 lg:py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="inline-flex items-center space-x-2 bg-warning/10 px-4 py-2 rounded-full mb-4">
              <Icon name="MapIcon" size={20} className="text-warning" />
              <span className="text-sm font-semibold text-warning">Implementation Roadmap</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-display font-bold text-foreground mb-4">
              Your AI Implementation Journey
            </h2>
            <p className="text-lg text-muted-foreground">
              A proven phase-by-phase approach to successful AI adoption, from discovery to optimization.
            </p>
          </div>
          
          <div className="space-y-6">
            {implementationPhases.map((phase, index) =>
            <div key={phase.id} className="relative">
                {index < implementationPhases.length - 1 &&
              <div className="absolute left-6 top-full h-6 w-0.5 bg-border hidden lg:block" />
              }
                <ImplementationPhaseCard {...phase} />
              </div>
            )}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-16 lg:py-20 bg-gradient-to-br from-primary to-secondary">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-display font-bold text-white mb-4">
            Ready to Transform Your Business with AI?
          </h2>
          <p className="text-lg text-white/90 mb-8">
            Schedule a consultation with our AI experts to discuss your specific needs and create a customized implementation plan.
          </p>
          
          <a
            href="/contact"
            className="inline-flex items-center justify-center px-8 py-4 bg-white text-primary rounded-lg font-semibold hover:bg-white/90 transition-all duration-300 shadow-elevation mb-12"
          >
            Get in Touch
          </a>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
              <Icon name="CheckCircleIcon" size={32} className="text-success mx-auto mb-2" variant="solid" />
              <div className="text-white font-semibold">Free Assessment</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
              <Icon name="UserGroupIcon" size={32} className="text-success mx-auto mb-2" variant="solid" />
              <div className="text-white font-semibold">Expert Guidance</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
              <Icon name="RocketLaunchIcon" size={32} className="text-success mx-auto mb-2" variant="solid" />
              <div className="text-white font-semibold">Proven Results</div>
            </div>
          </div>
        </div>
      </section>
    </div>);

};

export default AIHubInteractive;